import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { CDPrice as Price } from '~/components/Price/CDPrice';
import type { ProductViewProps } from '~/pages/Product/Product';
import { getApplicableMultibuyLevel } from '~/utils/multibuy';

import { ProductMixAndMatchPrice } from '../ProductMixAndMatchPrice';
import styles from './ProductPrice.module.scss';

const ProductPrice = ({
  isMultiBuy,
  currentPrice,
  currentItem,
  isLoading,
  count,
}: Pick<
  ProductViewProps,
  'isMultiBuy' | 'currentItem' | 'currentPrice' | 'isLoading' | 'count'
>) => {
  const applicableLevel = currentItem
    ? getApplicableMultibuyLevel(currentItem.multibuyPriceLevels, count)
    : null;
  const multibuyActivated = applicableLevel !== null;

  const discount = useMemo(() => {
    if (isMultiBuy && multibuyActivated) {
      return `${currentPrice}`;
    }

    if (currentItem?.discountPrice) {
      return currentItem?.priceFormatted;
    }
  }, [isMultiBuy, multibuyActivated, currentPrice, currentItem]);
  const hasDiscount = Boolean(discount);

  if (isLoading) {
    return (
      <div className={styles.price}>
        {!company.is('mcosmetic') && (
          <div className={styles.oldPrice}>
            <Skeleton containerClassName={styles.skeleton} />
          </div>
        )}
        <div
          className={classes([
            styles.currentPrice,
            hasDiscount && styles.currentPrice_discounted,
          ])}
        >
          <Skeleton containerClassName={styles.skeleton} />
        </div>
      </div>
    );
  }

  if (currentItem?.mixAndMatch) {
    return <ProductMixAndMatchPrice item={currentItem} />;
  }

  return (
    <Price
      price={currentPrice ?? 0}
      discount={isMultiBuy ? undefined : discount}
      percentDelimiter={isMultiBuy && !multibuyActivated}
      discountPercent={
        company.isIncludedIn([CompanyName.DubaiExpress])
          ? undefined
          : currentItem?.discountPercentage
      }
      classes={{
        root: styles.price,
        classNameOldPrice: styles.oldPrice,
        classNameCurrentPrice: styles.currentPrice,
        classNameDiscountPercent: styles.discountPercentage,
      }}
    />
  );
};

export default observer(ProductPrice);
